<template>
  <div v-if="trackValidation" class="w-full">
      <div class="vx-card p-8 w-full">
      <div class="flex flex-row gap-1 items-center">
          <h5 class="mb-0">{{ $t('admin.tracks.validation.valid') }}</h5>

          <feather-icon v-if="trackValidation.is_valid" icon="CheckIcon" class="text-success align-middle"/>
          <feather-icon v-else icon="XIcon" class="text-danger align-middle"/>
          <p class="mb-0">{{ statusType }}</p>

          <feather-icon v-if="invalidationChanged && manualInvalidationStatus !== 'unchanged'" icon="ArrowRightIcon" class="align-middle"/>
          <feather-icon v-if="invalidationChanged && manualInvalidationStatus === 'valid'" icon="CheckIcon" class="text-success align-middle"/>
          <feather-icon v-if="invalidationChanged && manualInvalidationStatus === 'invalid'" icon="XIcon" class="text-danger align-middle"/>
      </div>

      <div class="flex flex-row gap-2 items-center mt-4">
          <h5>{{ $t('admin.tracks.validation.change_validation') }}</h5>
          <button v-if="getMode() === 'validate'" class="sk-btn sk-btn-small sk-btn-danger rounded-lg mb-2" @click="invalidate">
              Invalidate
          </button>

          <button v-else class="sk-btn sk-btn-small sk-btn-success rounded-lg mb-2" @click="validate">
              Validate
          </button>
      </div>

      <div v-if="getMode() === 'invalidate'">
          <div v-if="trackValidation.invalidations.length > 0" class="mt-8">
              <h5>{{ $t('admin.tracks.validation.reasons') }}</h5>
              <ul>
                  <li v-for="invalidation in trackValidation.invalidations" :key="invalidation.id_track_type_invalidation">
                      {{ `${invalidation.name} (${invalidation.automated ? $t('admin.tracks.validation.automated') : $t('admin.tracks.validation.manual')}) ${invalidation.description ? invalidation.description : ''}` }}
                  </li>
              </ul>
          </div>

          <div v-if="trackValidation.overlaps.length > 0" class="mt-8">
              <h5>{{ $t('admin.tracks.validation.overlaps') }}</h5>
              <Datatable
                  :config="datatableConfig"
                  :elements="overlaps"
                  :stripe="true"
                  :sst="true"
              />
          </div>

          <div class="mt-8">
              <h5 class="mb-4">{{ $t('admin.tracks.validation.manual_invalidations') }}</h5>
              <vs-checkbox
                  v-for="invalidation in invalidations"
                  :key="invalidation.id"
                  class="ml-0 mt-2 text-base"
                  v-model="invalidation.checked"
              >
                  <p class="ml-1 mb-0">{{ invalidation.name }}</p>
              </vs-checkbox>
          </div>
      </div>

      <div class="mt-8">
          <h5 class="mb-4">{{ $t('admin.tracks.validation.invalidated_challenges') }}</h5>
          <Combobox
              class="mt-2 lg:w-1/3 md:w-2/3 sm:w-full"
              :options="challenges.map(challenge => ({key: challenge.id, text: `${challenge.title} (${challenge.id})`}))"
              :multiple="true"
              v-model="trackValidation.invalidated_challenges"
          />
      </div>

      <div class="flex flex-row gap-4 mt-12">
          <button class="sk-btn sk-btn-primary rounded-lg btn-border" @click="handleCancel">
              {{ $t('apps.modals.cancel')}}
          </button>
          <button :disabled="getMode() === 'invalidate' && invalidations.filter(i => i.checked).length === 0" class="sk-btn sk-btn-primary rounded-lg" @click="handleSave">
              {{ $t('Save')}}
          </button>
      </div>
      </div>

      <div v-if="trackStatistics" class="vx-card p-8 w-full mt-8">
          <h5 class="mb-4">Track statistics</h5>
          <div class="vx-row mb-4">
              <div class="vx-col w-full md:w-1/2">
                  <p class="mb-1"><strong>Date:</strong> {{ trackStatisticsDate }}</p>
                  <p class="mb-1"><strong>Activity:</strong> {{ trackStatisticsActivity }}</p>
                  <p class="mb-1"><strong>Distance:</strong> {{ trackStatisticsDistanceKm }}</p>
              </div>
              <div class="vx-col w-full md:w-1/2">
                  <p class="mb-1"><strong>Time:</strong> {{ trackStatisticsDuration }}</p>
              </div>
          </div>

          <h6 class="mb-3">Statistics per station</h6>
          <div v-if="trackStatisticsResortRows.length === 0" class="text-base">
              No station data
          </div>
          <div v-else class="overflow-x-auto">
              <table class="vs-table w-full">
                  <thead>
                  <tr>
                      <th class="text-left">Station</th>
                      <th class="text-left">Distance</th>
                      <th class="text-left">Distance %</th>
                      <th class="text-left">Time</th>
                      <th class="text-left">Time %</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="(resortRow, index) in trackStatisticsResortRows" :key="`${resortRow.station}-${index}`">
                      <td>{{ resortRow.station }}</td>
                      <td>{{ resortRow.distance }}</td>
                      <td>{{ resortRow.distancePercent }}</td>
                      <td>{{ resortRow.time }}</td>
                      <td>{{ resortRow.timePercent }}</td>
                  </tr>
                  </tbody>
              </table>
          </div>
      </div>
  </div>
</template>

<script>

import TrackService from "@/modules/Admin/Services/TrackService";
import Datatable from "@/modules/Shared/Components/table/SKDataTable.vue";
import {DateTime} from "luxon";
import {formatSeconds} from "@/modules/Shared/Helpers/dateHelper";
import ChallengeService from "@/modules/Admin/Services/ChallengeService";
import {unifyPaginatedResponse} from "@/modules/Shared/Helpers/apiResponseHelper";
import PaginationService from "@/modules/Shared/Services/PaginationService";
import Combobox from "@/modules/Shared/Components/Combobox.vue";
import loader from "@/modules/Shared/Mixins/loader";
import store from "@/modules/Shared/Store/store";

export default {
  name: "EditTrackValidationPage",
  components: {Combobox, Datatable},
  mixins: [loader],
  data() {
    return {
      mode: null,
      trackValidation: null,
      trackStatistics: null,
      invalidationChanged: false,
      invalidations: [],
      datatableConfig: [
          { 'type': 'image', 'field': 'thumbnail', head: this.$t("tracks.tracks-list.thumbnail"), 'searchable': false, 'sorteable': false },
          { 'type': 'text', 'field': 'valid', head: this.$t("admin.tracks.validation.valid"), 'searchable': false, 'sortable': false },
          { 'type': 'text', 'field': 'distance', head: this.$t("tracks.tracks-list.distance"), 'searchable': false, 'sorteable': false },
          { 'type': 'text', 'field': 'duration', head: this.$t("tracks.tracks-list.duration"), 'searchable': false, 'sorteable': false },
          { 'type': 'text', 'field': 'startdate', head: this.$t("banner.table.startdate"), 'searchable': false, 'sorteable': false },
          { 'type': 'text', 'field': 'enddate', head: this.$t("banner.table.enddate"), 'searchable': false, 'sorteable': false },
          { 'type': 'text', 'field': 'app', head: this.$t("dashboard.app"), 'searchable': false, 'sorteable': false },
      ]
    }
  },

  async beforeRouteEnter (to, from, next) {
      const trackValidation = await TrackService.getTrackValidation(to.params.uuid)

      await Promise.all(trackValidation.overlaps.map(overlap => 
        TrackService.getTrackStatistics(overlap.uuid)
          .then(stats => { overlap.statistics = stats })
          .catch(() => { overlap.statistics = {} })
      ));

      let trackStatistics = null

      try {
          trackStatistics = await TrackService.getTrackStatistics(to.params.uuid)
      } catch (error) {
          console.error(error)
      }

      await store.dispatch('setRouteElement', trackValidation)
      next(vm => {
          vm.trackValidation = trackValidation
          vm.trackStatistics = trackStatistics
      })
  },
  computed: {
      manualInvalidationStatus() {
          if (this.getMode() === 'validate') {
              return 'valid'
          }

          if (this.getMode() === 'invalidate' && this.invalidations.filter(invalidation => invalidation.checked).length > 0) {
              return 'invalid'
          }

          return 'unchanged'
      },
      statusType() {
          if (this.trackValidation.is_valid) {
              return this.trackValidation.invalidations.length > 0 ? this.$t('admin.tracks.validation.manual') : this.$t('admin.tracks.validation.automated')
          }

          return this.trackValidation.invalidations.some(invalidation => !invalidation.automated) ? this.$t('admin.tracks.validation.manual') : this.$t('admin.tracks.validation.automated')
      },
      overlaps() {
          return this.trackValidation.overlaps.map(overlap => ({
              thumbnail: overlap.thumbnail,
              valid: overlap.is_valid ? '✅' : '❌',
              distance: overlap.statistics.distance ? Math.round(overlap.statistics.distance) + ' m' : '',
              duration: overlap.statistics.duration ? formatSeconds(overlap.statistics.duration) : '',
              startdate: DateTime.fromSeconds(overlap.statistics.timestamp_start).toFormat('dd-LL-yyyy hh:mm:ss'),
              enddate: DateTime.fromSeconds(overlap.statistics.timestamp_end).toFormat('dd-LL-yyyy hh:mm:ss'),
              app: overlap.app.name
          }))
      },
      trackStatisticsDate() {
          if (!this.trackStatistics || !this.trackStatistics.timestamp_start) {
              return '-'
          }

          return DateTime.fromSeconds(this.trackStatistics.timestamp_start).toFormat('dd-LL-yyyy HH:mm:ss')
      },
      trackStatisticsActivity() {
          return (this.trackStatistics && this.trackStatistics.activity) ? this.trackStatistics.activity : '-'
      },
      trackStatisticsDistanceKm() {
          if (!this.trackStatistics || !this.trackStatistics.distance) {
              return '0 km'
          }

          return `${(this.trackStatistics.distance / 1000).toFixed(2)} km`
      },
      trackStatisticsDuration() {
          if (!this.trackStatistics || !this.trackStatistics.duration) {
              return '-'
          }

          return formatSeconds(this.trackStatistics.duration)
      },

      trackStatisticsResortRows() {
          if (!this.trackStatistics || !Array.isArray(this.trackStatistics.resorts)) {
              return []
          }

          const totalDistance = this.trackStatistics.distance || 0
          const totalDuration = this.trackStatistics.duration || 0

          return this.trackStatistics.resorts.map((resort) => {
              const resortDistance = resort.distance || 0
              const resortDuration = resort.duration || 0
              const stationName = resort.resort && resort.resort.name ? resort.resort.name : '-'

              return {
                  station: stationName,
                  distance: `${(resortDistance / 1000).toFixed(2)} km`,
                  distancePercent: this.getPercentLabel(resortDistance, totalDistance),
                  time: resortDuration ? formatSeconds(resortDuration) : '-',
                  timePercent: this.getPercentLabel(resortDuration, totalDuration),
              }
          })
      }
  },

  methods: {
      getPercentLabel(value, total) {
          if (!total) {
              return '0%'
          }

          return `${((value / total) * 100).toFixed(1)}%`
      },
      getMode() {
          if (this.mode) {
              return this.mode
          }

          return this.trackValidation.is_valid ? 'validate' : 'invalidate'
      },
      validate() {
          this.invalidations = this.invalidations.map(invalidation => ({...invalidation, checked: false}))
          this.mode = 'validate'
          this.invalidationChanged = true
      },
      invalidate() {
          this.mode = 'invalidate'
          this.invalidationChanged = true
      },
      handleCancel() {
          this.$router.push({ name: 'admin-tracks' });
      },

      async handleSave() {
          await this.loadAndNotify(async () => {
              const invalidations = this.invalidations
                  .filter(invalidation => invalidation.checked)
                  .map(invalidation => invalidation.id)

              const body = {
                  valid: invalidations.length === 0,
                  invalidations: invalidations,
                  invalidated_challenges: this.trackValidation.invalidated_challenges
              }

              try {
                  await TrackService.updateTrackValidation(this.$route.params.uuid, body)
                  await this.$router.push({ name: 'admin-tracks' });
              } catch (error) {
                  console.error(error)
              }
          })
      }
  },

  async created() {
      await this.loadAndNotifyError(async () => {
          const [invalidationTypes, challenges] = await Promise.all([
              TrackService.getInvalidationTypes(),
              unifyPaginatedResponse(ChallengeService.getChallenges, PaginationService.parseParameters({
                  filters: [
                      { field: 'publishend', value: DateTime.now().toFormat('yyyy-MM-dd HH:mm:ss') }
                  ],
                  per_page: 30
              }))
          ])

          this.challenges = challenges

          this.invalidations = invalidationTypes.data.map(invalidationType => {
              const invalidation = this.trackValidation.invalidations.find(invalidation => invalidation.id_track_type_invalidation === invalidationType.id)
              return {
                  ...invalidationType,
                  checked: invalidation && !invalidation.automated
              }
          })

          this.mode = this.trackValidation.is_valid ? 'validate' : 'invalidate'
      })
  },
};
</script>
